/**
 * Persistence for HUD layout choices (issue #74): the Layers drawer's
 * open/closed state and per-layer visibility. Pure and storage-injected, same
 * shape as core/quality.ts's persistence helpers (issue #72), so the rules are
 * unit-tested in Node; main.ts owns the DOM wiring and the real localStorage.
 */

/** The layers with a drawer checkbox and persisted visibility. */
export type LayerId = 'buildings' | 'water' | 'walls' | 'vegetation' | 'beacons' | 'bearings';

const LAYER_IDS: readonly LayerId[] = ['buildings', 'water', 'walls', 'vegetation', 'beacons', 'bearings'];

function isLayerId(value: string): value is LayerId {
  return (LAYER_IDS as readonly string[]).includes(value);
}

/**
 * Storage-like interface for persisting HUD prefs. Deliberately narrow
 * (get/set only) and injected rather than imported so this module never
 * touches `window.localStorage` directly and stays Node-testable. Both methods
 * may throw (e.g. `SecurityError` in private browsing); callers of the helpers
 * below must not need to catch — the helpers absorb that themselves.
 */
export interface HudPrefsStorage {
  get(key: string): string | null | undefined;
  set(key: string, value: string): void;
}

export const DRAWER_OPEN_STORAGE_KEY = 'ray2.layersDrawerOpen';
export const LAYER_VISIBILITY_STORAGE_KEY = 'ray2.layerVisibility';
export const CONTROLS_OVERLAY_VISIBLE_STORAGE_KEY = 'ray2.controlsOverlayVisible';

/** Reads the persisted Layers drawer open/closed state. Defaults to false
 * (closed) when nothing is saved, the saved value doesn't parse, or storage
 * access itself throws. */
export function loadDrawerOpen(storage: HudPrefsStorage): boolean {
  try {
    return storage.get(DRAWER_OPEN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Persists the drawer open/closed state. Returns true on success, false if
 * storage access threw; never throws itself. */
export function saveDrawerOpen(storage: HudPrefsStorage, open: boolean): boolean {
  try {
    storage.set(DRAWER_OPEN_STORAGE_KEY, open ? 'true' : 'false');
    return true;
  } catch {
    return false;
  }
}

/**
 * Reads persisted per-layer visibility. Returns an empty map (never throws)
 * when nothing is saved, the JSON is corrupted or not an object, or storage
 * access itself throws. A key that isn't a recognised {@link LayerId} (a layer
 * that no longer exists), or a value that isn't a boolean, is dropped silently
 * rather than surfacing a corrupted entry.
 */
export function loadLayerVisibility(storage: HudPrefsStorage): Partial<Record<LayerId, boolean>> {
  try {
    const raw = storage.get(LAYER_VISIBILITY_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const result: Partial<Record<LayerId, boolean>> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (isLayerId(key) && typeof value === 'boolean') result[key] = value;
    }
    return result;
  } catch {
    return {};
  }
}

/** Persists per-layer visibility. Returns true on success, false if storage
 * access threw; never throws itself. */
export function saveLayerVisibility(
  storage: HudPrefsStorage,
  visibility: Partial<Record<LayerId, boolean>>,
): boolean {
  try {
    storage.set(LAYER_VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
    return true;
  } catch {
    return false;
  }
}

/**
 * Resolves whether a layer should be visible at boot. An unavailable layer
 * (its data file is missing — hard constraint #7) is always invisible
 * regardless of any persisted choice: the disabled row wins. An available
 * layer uses the persisted choice if one exists, else the caller's own default
 * for that layer (each layer already has one independent of persistence, e.g.
 * road walls default to hidden below the high quality tier).
 */
export function resolveLayerVisibility(params: {
  available: boolean;
  persisted: boolean | undefined;
  defaultVisible: boolean;
}): boolean {
  if (!params.available) return false;
  return params.persisted ?? params.defaultVisible;
}

/**
 * Reads the persisted controls-overlay (keyboard legend, issue #87)
 * shown/hidden choice. Unlike the Layers drawer, this defaults to VISIBLE —
 * both when nothing is saved and when storage access itself throws (private
 * browsing): the legend is a discoverability aid, so the safe default is to
 * show it rather than hide it.
 */
export function loadControlsOverlayVisible(storage: HudPrefsStorage): boolean {
  try {
    const raw = storage.get(CONTROLS_OVERLAY_VISIBLE_STORAGE_KEY);
    return raw !== 'false';
  } catch {
    return true;
  }
}

/** Persists the controls-overlay shown/hidden choice. Returns true on
 * success, false if storage access threw; never throws itself. */
export function saveControlsOverlayVisible(storage: HudPrefsStorage, visible: boolean): boolean {
  try {
    storage.set(CONTROLS_OVERLAY_VISIBLE_STORAGE_KEY, visible ? 'true' : 'false');
    return true;
  } catch {
    return false;
  }
}
